import { useCallback, useEffect, useRef, useState } from 'react';

import { GATE_STAGE } from '../constants/geolocationCopy';
import {
  GEO_ERROR,
  GEO_RETRY_OPTIONS,
  getGeolocationPermissionState,
  getKioskGeolocation,
  isGeoSuccess,
  subscribeToPermissionChange,
} from '../helpers/getKioskGeolocation';

/**
 * Mandatory-GPS gate: `request()` resolves with coordinates, or `null` when the worker cancels.
 * With `requireLocation` off it resolves `{}` without ever touching the geolocation API.
 */
export const useLocationGate = ({ requireLocation }) => {
  const [stage, setStage] = useState(null);
  const resolver = useRef(null);

  const settle = useCallback((value) => {
    setStage(null);
    if (resolver.current) resolver.current(value);
    resolver.current = null;
  }, []);

  const capture = useCallback(
    (options) => {
      if (!window.isSecureContext) return setStage(GATE_STAGE.blocked);
      setStage(GATE_STAGE.requesting);
      getKioskGeolocation(options).then(async (result) => {
        if (isGeoSuccess(result)) return settle(result);
        if (result.error !== GEO_ERROR.PERMISSION_DENIED) return setStage(GATE_STAGE.failed);
        // A denial while the site permission reads 'granted' means the OS blocks location, not the browser.
        const permission = await getGeolocationPermissionState();
        setStage(permission === 'granted' ? GATE_STAGE.failed : GATE_STAGE.blocked);
      });
    },
    [settle]
  );

  useEffect(() => {
    if (stage !== GATE_STAGE.blocked || !window.isSecureContext) return;
    return subscribeToPermissionChange((state) => state === 'granted' && capture());
  }, [stage, capture]);

  const request = useCallback(() => {
    if (!requireLocation) return Promise.resolve({});
    setStage(GATE_STAGE.required);
    return new Promise((resolve) => {
      resolver.current = resolve;
    });
  }, [requireLocation]);

  // Runs inside the tap handler with no preceding await — iOS drops the native prompt otherwise.
  const onPrimary = useCallback(() => {
    capture(stage === GATE_STAGE.failed ? GEO_RETRY_OPTIONS : undefined);
  }, [stage, capture]);

  return {
    request,
    modalProps: { stage, onPrimary, onCancel: () => settle(null) },
  };
};
